import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMyPayeeTargets } from "@/hooks/useMyPayeeTargets";
import type { InvoiceRow } from "@/hooks/useInvoices";
import { queryKeys } from "@/lib/queryKeys";

export type MyPayeeInvoiceRow = InvoiceRow & {
  paid_at?: string | null;
  stripe_payment_intent_id?: string | null;
  property_name?: string | null;
};

/** Invoices where the current user is the payee (cleaner or company admin). RLS restricts to own invoices. Returns all then caller can split by pending/paid. */
export function useMyPayeeInvoices() {
  const { data: targets, isSuccess: targetsReady } = useMyPayeeTargets();

  return useQuery({
    queryKey: [...queryKeys.invoices.myPayeeInvoices("pending"), targets?.cleanerId, targets?.companyIds],
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<MyPayeeInvoiceRow[]> => {
      const hasTargets =
        (targets?.cleanerId != null) || (targets?.companyIds?.length ? true : false);
      if (!hasTargets) return [];

      const { data, error } = await supabase
        .from("invoices")
        .select(
          "id, property_id, owner_user_id, payee_type, payee_id, status, period_start, period_end, total_amount_cents, created_at, updated_at, paid_at, stripe_payment_intent_id"
        )
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data ?? []) as MyPayeeInvoiceRow[];
    },
    enabled: targetsReady && ((targets?.cleanerId != null) || (targets?.companyIds?.length ?? 0) > 0),
  });
}

export function pendingInvoices(invoices: MyPayeeInvoiceRow[]): MyPayeeInvoiceRow[] {
  return invoices.filter(
    (inv) => (inv.status === "draft" || inv.status === "sent") && !inv.paid_at
  );
}

export function paidInvoices(invoices: MyPayeeInvoiceRow[]): MyPayeeInvoiceRow[] {
  return invoices.filter((inv) => inv.status === "paid" || inv.paid_at != null);
}
